<template>
    <a-card :bordered="false" class="bl-card" :style="selectedOfferKeys.length && { marginBottom: '60px' }">
        <div class="bl-filterbar">
            <a-input
                class="bl-search-input"
                placeholder="Buscar por loja ou fornecedor"
                v-model="search"
                allow-clear
            />
        </div>
        <a-table
            size="small"
            :columns="columns"
            :data-source="rows"
            :loading="is_loading"
            row-key="store_id"
            :expandedRowKeys="expanded"
            :expand-icon-column-index="1"
            :locale="{ emptyText: 'Nenhum pré-pedido pendente.' }"
            @expand="onExpandChange"
            :pagination="false"
        >
            <template slot="selecionar" slot-scope="text, record">
                <a-checkbox
                    :checked="isLojaFullySelected(record)"
                    :indeterminate="isLojaPartiallySelected(record)"
                    @change="toggleLojaSelection(record, $event.target.checked)"
                />
            </template>
            <template slot="loja" slot-scope="text, record">
                <div class="bl-strong">{{ record.store_name }}</div>
            </template>
            <template slot="fornecedores" slot-scope="text, record">{{ record.pendingProviders.length }}</template>
            <template slot="valor" slot-scope="text, record">{{ record.pendingTotal | customCurrency }}</template>
            <template slot="acoes" slot-scope="text, record">
                <a-button
                    class="bl-btn"
                    :loading="!!sending[record.store_id]"
                    :disabled="!!sending[record.store_id]"
                    @click="sendAll(record)"
                >
                    Enviar todos
                </a-button>
            </template>

            <template slot="expandedRowRender" slot-scope="record">
                <div class="expanded-table-wrapper">
                <a-table
                    size="small"
                    :columns="innerColumns"
                    :data-source="record.pendingProviders"
                    :pagination="false"
                    row-key="key"
                    :expand-icon-column-index="1"
                    @expand="(expanded, provider) => onProviderExpand(expanded, record, provider)"
                >
                    <template slot="selecionar" slot-scope="text, provider">
                        <a-checkbox
                            :checked="isProviderFullySelected(record, provider)"
                            :indeterminate="isProviderPartiallySelected(record, provider)"
                            @change="toggleProviderSelection(record, provider, $event.target.checked)"
                        />
                    </template>
                    <template slot="fornecedor" slot-scope="text, provider">
                        <div class="bl-sup">
                            <span class="bl-n">
                                {{ provider.provider.name }}<template v-if="provider.provider.cnpj"> · {{ provider.provider.cnpj }}</template>
                            </span>
                            <span class="bl-v" v-if="provider.seller.name">
                                {{ provider.seller.name }}<template v-if="provider.seller.phone"> · {{ provider.seller.phone }}</template>
                            </span>
                        </div>
                    </template>
                    <template slot="cond" slot-scope="text, provider">{{ provider.delivery ? provider.delivery.payment_term || "-" : "-" }}</template>
                    <template slot="entrega" slot-scope="text, provider">{{ deliveryDate(provider) }}</template>
                    <template slot="faturamentoMinimo" slot-scope="text, provider">
                        <span v-if="provider.delivery && provider.delivery.minimum_billing">{{ provider.delivery.minimum_billing | customCurrency }}</span>
                        <span v-else>-</span>
                    </template>
                    <template slot="valor" slot-scope="text, provider">
                        <span :class="{ 'bl-below-min': isBelowMin(provider) }">{{ provider.total | customCurrency }}</span>
                    </template>
                    <template slot="acoes" slot-scope="text, provider">
                        <a-button
                            class="bl-btn"
                            :loading="!!sending[record.store_id + '-' + provider.seller.id]"
                            :disabled="!!sending[record.store_id + '-' + provider.seller.id]"
                            @click="sendOne(record, provider)"
                        >
                            Enviar pedido
                        </a-button>
                    </template>

                    <template slot="expandedRowRender" slot-scope="provider">
                        <div class="expanded-table-wrapper">
                            <cc-loader-full v-if="loadingProducts[offerKey(record, provider)]" />

                            <a-table
                                v-else
                                size="small"
                                :columns="productColumns"
                                :data-source="productsByOffer[offerKey(record, provider)] || []"
                                :pagination="false"
                                :row-key="(product) => productKey(product, record, provider)"
                                :row-selection="productRowSelection(record, provider)"
                                :locale="{ emptyText: 'Nenhum produto pendente.' }"
                            >
                                <template slot="nome" slot-scope="text, product">
                                    <div class="vpm-pname">{{ product.name }}</div>
                                    <div class="vpm-pean" v-if="product.ean || product.internal_code">
                                        <template v-if="product.ean">EAN {{ product.ean }}</template>
                                        <template v-if="product.ean && product.internal_code"> · </template>
                                        <template v-if="product.internal_code">Cód. {{ product.internal_code }}</template>
                                    </div>
                                </template>

                                <template slot="pedido" slot-scope="text, product">
                                    <a-input
                                        :min="0"
                                        class="matrix-qty-input"
                                        v-model="product.quantity"
                                        @input="handleQuantityInput($event, product)"
                                        @blur="saveOfferQuantity(product, record, provider)"
                                    />
                                </template>

                                <template slot="digitacao" slot-scope="text, product">
                                    {{ product.offer_price | customCurrency }}
                                </template>

                                <template slot="ultimaCompra" slot-scope="text, product">
                                    <span v-if="product.last_purchase">{{ product.last_purchase | customCurrency }}</span>
                                    <span v-else>—</span>
                                </template>

                                <template slot="diferenca" slot-scope="text, product">
                                    <span v-if="product.last_purchase" :class="deltaClass(product)">
                                        {{ (product.offer_price - product.last_purchase) | customCurrency }}
                                    </span>
                                    <span v-else>—</span>
                                </template>

                                <template slot="variacao" slot-scope="text, product">
                                    <span v-if="product.last_purchase" :class="deltaClass(product)">
                                        {{ variation(product).toFixed(1) }}%
                                    </span>
                                    <span v-else>—</span>
                                </template>

                                <template slot="total" slot-scope="text, product">
                                    {{ product.total | customCurrency }}
                                </template>

                                <template slot="acoes" slot-scope="text, product">
                                    <ActionMenu :actions="productActions(product, record, provider)" />
                                </template>
                            </a-table>
                        </div>
                    </template>
                </a-table>
                </div>
            </template>
        </a-table>

        <ViewProductsModal
            v-if="productsModal.open"
            :quotation-id="$route.params.id"
            :store-id="productsModal.storeId"
            :store-name="productsModal.storeName"
            :provider-id="productsModal.providerId"
            :provider-name="productsModal.providerName"
            :seller-id="productsModal.sellerId"
            :seller-name="productsModal.sellerName"
            @close="productsModal.open = false"
            @giro="(payload) => $emit('giro', payload)"
        />

        <NegotiationModal
            v-if="negotiationModal.open"
            :product="negotiationModal.product"
            :typing="negotiationModal.winner"
            :quotation_id="$route.params.id"
            @close="closeNegotiation"
        />

        <ProductWrapModal
            v-if="wrapModal.open"
            :product="wrapModal.product"
            :salesman="wrapModal.winner"
            @close="closeWrap"
        />

        <ProductOrdersModal
            v-if="historyModal.open"
            :product_id="historyModal.productId"
            :product_name="historyModal.productName"
            @close="historyModal.open = false"
        />

        <SelectionFooter
            :count="selectedOffers.length"
            item-label="ofertas selecionadas"
        >
            <template slot="summary">
                <strong>{{ selectedTotal | customCurrency }}</strong>
            </template>

            <template slot="actions">
                <a-button size="large" @click="setSelection([])">Limpar seleção</a-button>
                <a-button size="large" @click="bulkExport">Exportar</a-button>
                <a-button size="large" @click="bulkZeroQuantity">Zerar quantidade</a-button>
                <a-button size="large" type="primary" @click="bulkGenerateOrder">Gerar pedido</a-button>
            </template>
        </SelectionFooter>
    </a-card>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import RequestsService from "@/services/v3/request.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import currencyPrecision from "@/mixins/currencyPrecision";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ViewProductsModal from "./_view-products.modal.vue";
import NegotiationModal from "@/modals/negotiate-offer/negotiate-offer.modal.vue";
import ProductWrapModal from "@/modals/product-wrap/product-wrap.modal.vue";
import ProductOrdersModal from "@/modals/products/orders";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import SelectionFooter from "@/components/cliente/monitors/SelectionFooter.vue";

export default {
    name: "ByLojaTable",
    mixins: [currencyPrecision, loaderMixin],
    components: { ViewProductsModal, NegotiationModal, ProductWrapModal, ProductOrdersModal, ActionMenu, SelectionFooter },
    props: {
        // Flat rows from GET clients/requests/{id}/pre-orders: one per
        // (loja, vendedor, fornecedor). Already filtered to pending only.
        pre_orders: { type: Array, default: () => [] },
        is_loading: { type: Boolean, default: false },
        pendencyOnly: { type: Boolean, default: false },
        // Shared with Por Produto / Analítico via `.sync` on the parent
        // (_pre-orders.tab.vue) — same row-key shape in all three:
        // `${produto}-${loja}-${fornecedor}-${vendedor}`.
        selectedOfferKeys: { type: Array, default: () => [] },
    },
    data() {
        return {
            svc: new QuotationService(),
            v3Svc: new RequestsService(),
            expanded: [],
            sending: {},
            productsModal: {
                open: false,
                storeId: null,
                storeName: "",
                providerId: null,
                providerName: "",
                sellerId: null,
                sellerName: "",
            },
            productsByOffer: {},
            loadingProducts: {},
            negotiationModal: {
                open: false,
                winner: null,
                product: null,
            },
            wrapModal: {
                open: false,
                winner: null,
                product: null,
            },
            historyModal: {
                open: false,
                productId: null,
                productName: "",
            },
            search: "",
        };
    },
    computed: {
        productColumns() {
            return [
                { title: "Produto", key: "nome", width: 220, scopedSlots: { customRender: "nome" } },
                { title: "Qtd. Emb.", key: "packing", width: 90, align: "center", customRender: (t, p) => p.packing || "—" },
                { title: "Requisição", key: "requisicao", width: 90, align: "center", customRender: (t, p) => (p.requested_quantity != null ? p.requested_quantity : "—") },
                { title: "Pedido", key: "pedido", width: 90, align: "center", scopedSlots: { customRender: "pedido" } },
                { title: "Und. compra", key: "purchase_unity", width: 100, align: "center", customRender: (t, p) => p.purchase_unity || "—" },
                { title: "Estoque", key: "inventory", width: 90, align: "center", customRender: (t, p) => (p.inventory != null ? p.inventory : "—") },
                { title: "Digitação", key: "digitacao", width: 100, align: "right", scopedSlots: { customRender: "digitacao" } },
                { title: "Últ. Compra", key: "ultimaCompra", width: 100, align: "right", scopedSlots: { customRender: "ultimaCompra" } },
                { title: "Diferença", key: "diferenca", width: 100, align: "right", scopedSlots: { customRender: "diferenca" } },
                { title: "Variação", key: "variacao", width: 90, align: "right", scopedSlots: { customRender: "variacao" } },
                { title: "Total", key: "total", width: 110, align: "right", scopedSlots: { customRender: "total" } },
                { title: "Ações", key: "acoes", width: 60, scopedSlots: { customRender: "acoes" } },
            ];
        },
        columns() {
            return [
                { title: "", key: "selecionar", width: 40, align: "center", scopedSlots: { customRender: "selecionar" } },
                { title: "Loja", key: "loja", scopedSlots: { customRender: "loja" } },
                { title: "Fornecedores pendentes", key: "fornecedores", align: "right", scopedSlots: { customRender: "fornecedores" } },
                { title: "Valor total", key: "valor", align: "right", scopedSlots: { customRender: "valor" } },
                { title: "Ações", key: "acoes", scopedSlots: { customRender: "acoes" } },
            ];
        },
        innerColumns() {
            return [
                { title: "", key: "selecionar", width: 40, align: "center", scopedSlots: { customRender: "selecionar" } },
                { title: "Fornecedor", key: "fornecedor", scopedSlots: { customRender: "fornecedor" } },
                { title: "Cond. pagamento", key: "cond", scopedSlots: { customRender: "cond" } },
                { title: "Entrega prevista", key: "entrega", scopedSlots: { customRender: "entrega" } },
                { title: "Fat. mín.", key: "faturamentoMinimo", align: "right", scopedSlots: { customRender: "faturamentoMinimo" } },
                { title: "Valor", key: "valor", align: "right", scopedSlots: { customRender: "valor" } },
                { title: "Ações", key: "acoes", scopedSlots: { customRender: "acoes" } },
            ];
        },
        rows() {
            // Group the flat rows by loja. The endpoint returns pending rows
            // only, so every row here is a pending fornecedor by construction —
            // there is no possuiPedido to filter on any more.
            const map = {};
            const term = (this.search || "").trim().toLowerCase();

            (this.pre_orders || []).forEach((po) => {
                if (this.pendencyOnly && !po.below_minimum) return;

                const storeMatches = !term || (po.store.name || "").toLowerCase().includes(term);
                const providerMatches =
                    !term ||
                    (po.provider.name || "").toLowerCase().includes(term) ||
                    (po.seller.name || "").toLowerCase().includes(term);

                if (!storeMatches && !providerMatches) return;

                if (!map[po.store.id]) {
                    map[po.store.id] = {
                        store_id: po.store.id,
                        store_name: po.store.name,
                        pendingProviders: [],
                        pendingTotal: 0,
                    };
                }

                // ant-design needs a stable row-key; the payload has no id of
                // its own, and (fornecedor, vendedor) is unique within a loja.
                map[po.store.id].pendingProviders.push({
                    ...po,
                    key: po.provider.id + "-" + po.seller.id,
                });
                map[po.store.id].pendingTotal += po.total || 0;
            });

            return Object.values(map).filter((row) => row.pendingProviders.length);
        },
        // Only reflects fornecedores whose products have actually been
        // fetched (productsByOffer) — a selection made via the loja/fornecedor
        // "select all" checkboxes always loads first (see toggleLojaSelection/
        // toggleProviderSelection), so anything truly selected is loaded here.
        selectedOffers() {
            const keys = new Set(this.selectedOfferKeys);
            const offers = [];

            this.rows.forEach((record) => {
                record.pendingProviders.forEach((provider) => {
                    const products = this.productsByOffer[this.offerKey(record, provider)] || [];

                    products.forEach((product) => {
                        if (!keys.has(this.productKey(product, record, provider))) return;

                        offers.push({
                            ...product,
                            store_id: record.store_id,
                            store_name: record.store_name,
                            provider_id: provider.provider.id,
                            provider_name: provider.provider.name,
                            seller_id: provider.seller.id,
                            seller_name: provider.seller.name,
                        });
                    });
                });
            });

            return offers;
        },
        selectedTotal() {
            return this.selectedOffers.reduce((sum, offer) => sum + (offer.total || 0), 0);
        },
    },
    methods: {
        onExpandChange(isExpanded, record) {
            const idx = this.expanded.indexOf(record.store_id);
            if (isExpanded && idx === -1) this.expanded.push(record.store_id);
            else if (!isExpanded && idx !== -1) this.expanded.splice(idx, 1);
        },
        // below_minimum arrives precomputed, and already guards the case where
        // the seller has no minimum (faturamento_minimo 0 or no delivery row).
        isBelowMin(provider) {
            return !!provider.below_minimum;
        },
        deliveryDate(provider) {
            if (!provider.delivery || !provider.delivery.expected_date) return "-";
            return this.$options.filters.formatDateOnly(provider.delivery.expected_date);
        },
        offerKey(record, provider) {
            return `${record.store_id}-${provider.key}`;
        },
        onProviderExpand(expanded, record, provider) {
            if (!expanded) return;
            this.loadOfferProducts(record, provider);
        },
        // Shared by the expand handler and the selection checkboxes below —
        // both need the product list, and both must tolerate it not being
        // loaded yet (a fornecedor row can be select-all'd without ever
        // having been expanded).
        loadOfferProducts(record, provider) {
            const key = this.offerKey(record, provider);

            if (this.productsByOffer[key]) {
                return Promise.resolve(this.productsByOffer[key]);
            }

            this.$set(this.loadingProducts, key, true);

            return this.v3Svc
                .pre_orders_products(this.$route.params.id, {
                    store_id: record.store_id,
                    provider_id: provider.provider.id,
                    seller_id: provider.seller.id,
                })
                .then((response) => {
                    const products = response.data || [];
                    this.$set(this.productsByOffer, key, products);
                    return products;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                    return [];
                })
                .finally(() => {
                    this.$set(this.loadingProducts, key, false);
                });
        },
        productKey(product, record, provider) {
            return `${product.id}-${record.store_id}-${provider.provider.id}-${provider.seller.id}`;
        },
        setSelection(keys) {
            this.$emit('update:selectedOfferKeys', keys);
        },
        productRowSelection(record, provider) {
            const products = this.productsByOffer[this.offerKey(record, provider)] || [];
            const keys = products.map((p) => this.productKey(p, record, provider));

            return {
                selectedRowKeys: keys.filter((key) => this.selectedOfferKeys.includes(key)),
                onChange: (selectedRowKeys) => {
                    const otherKeys = this.selectedOfferKeys.filter((key) => !keys.includes(key));
                    this.setSelection([...otherKeys, ...selectedRowKeys]);
                },
            };
        },
        isProviderFullySelected(record, provider) {
            const products = this.productsByOffer[this.offerKey(record, provider)];
            if (!products || !products.length) return false;
            return products.every((p) => this.selectedOfferKeys.includes(this.productKey(p, record, provider)));
        },
        isProviderPartiallySelected(record, provider) {
            const products = this.productsByOffer[this.offerKey(record, provider)];
            if (!products || !products.length) return false;
            const selectedCount = products.filter((p) => this.selectedOfferKeys.includes(this.productKey(p, record, provider))).length;
            return selectedCount > 0 && selectedCount < products.length;
        },
        toggleProviderSelection(record, provider, checked) {
            this.loadOfferProducts(record, provider).then((products) => {
                const keys = products.map((p) => this.productKey(p, record, provider));

                this.setSelection(checked
                    ? Array.from(new Set([...this.selectedOfferKeys, ...keys]))
                    : this.selectedOfferKeys.filter((key) => !keys.includes(key)));
            });
        },
        isLojaFullySelected(record) {
            const providers = record.pendingProviders;
            return providers.length > 0 && providers.every((provider) => this.isProviderFullySelected(record, provider));
        },
        isLojaPartiallySelected(record) {
            const providers = record.pendingProviders;
            const anySelected = providers.some((provider) => this.isProviderFullySelected(record, provider) || this.isProviderPartiallySelected(record, provider));
            return anySelected && !this.isLojaFullySelected(record);
        },
        toggleLojaSelection(record, checked) {
            const providers = record.pendingProviders;

            Promise.all(providers.map((provider) => this.loadOfferProducts(record, provider))).then((productsLists) => {
                const keys = productsLists.flatMap((products, index) =>
                    products.map((p) => this.productKey(p, record, providers[index]))
                );

                this.setSelection(checked
                    ? Array.from(new Set([...this.selectedOfferKeys, ...keys]))
                    : this.selectedOfferKeys.filter((key) => !keys.includes(key)));
            });
        },
        bulkGenerateOrder() {
            if (!this.selectedOffers.length) return;

            const groups = {};

            this.selectedOffers.forEach((offer) => {
                const key = `${offer.store_id}-${offer.provider_id}-${offer.seller_id}`;
                if (!groups[key]) {
                    groups[key] = { store_id: offer.store_id, seller_id: offer.seller_id };
                }
            });

            const groupList = Object.values(groups);

            this.confirm_action({
                message: `Gerar pedido para ${groupList.length} combinação(ões) de loja/fornecedor?`,
                confirm_text: "Gerar pedidos",
                callback: () => {
                    Promise.all(
                        groupList.map((group) =>
                            this.svc.order({
                                idcotacao: this.$route.params.id,
                                loja: group.store_id,
                                idvendedor: group.seller_id,
                            })
                        )
                    )
                        .then(() => {
                            this.snack("Pedidos gerados com sucesso.");
                            this.setSelection([]);
                            this.$emit("reload");
                        })
                        .catch((error) => {
                            this.snack("Não foi possível gerar um ou mais pedidos.", "error");
                            ErrorHandlerService.handle(error, this.$store);
                        });
                },
            });
        },
        bulkZeroQuantity() {
            if (!this.selectedOffers.length) return;

            this.confirm_action({
                message: `Zerar a quantidade de ${this.selectedOffers.length} oferta(s) selecionada(s)?`,
                confirm_text: "Zerar quantidade",
                callback: () => {
                    Promise.all(
                        this.selectedOffers.map((offer) => {
                            offer.quantity = 0;

                            return this.svc.sendQuantidade({
                                numerocotacao: this.$route.params.id,
                                quantidade: 0,
                                vendedor: offer.seller_id,
                                produto: offer.id,
                                loja: offer.store_id,
                            });
                        })
                    )
                        .then(() => {
                            this.snack("Quantidades zeradas com sucesso.");
                            this.setSelection([]);
                        })
                        .catch((error) => {
                            this.snack("Não foi possível zerar uma ou mais quantidades.", "error");
                            ErrorHandlerService.handle(error, this.$store);
                        });
                },
            });
        },
        bulkExport() {
            if (!this.selectedOffers.length) return;

            const headers = ["Produto", "Fornecedor", "Vendedor", "Loja", "Valor", "Quantidade", "Total"];

            const rows = this.selectedOffers.map((offer) => [
                offer.name,
                offer.provider_name,
                offer.seller_name || "",
                offer.store_name,
                offer.offer_price,
                offer.quantity || 0,
                offer.total || 0,
            ]);

            const csv = [headers, ...rows]
                .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(";"))
                .join("\n");

            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = `pre-pedidos-por-loja-${this.$route.params.id}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            this.setSelection([]);
        },
        handleQuantityInput(event, product) {
            let value = event.target.value.replace(/\D/g, '');

            value = value.replace(/^0+(?=\d)/, '');

            if (+value > 999999) {
                value = '999999';
            }

            product.quantity = value;
            event.target.value = value;
        },
        saveOfferQuantity(product, record, provider) {
            const quantidade = Math.max(0, Number(product.quantity) || 0);

            this.svc
                .sendQuantidade({
                    numerocotacao: this.$route.params.id,
                    quantidade: quantidade || "0",
                    vendedor: provider.seller.id,
                    produto: product.id,
                    loja: record.store_id,
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        deltaClass(product) {
            if (product.offer_price > product.last_purchase) return "vpm-up";
            if (product.offer_price < product.last_purchase) return "vpm-down";
            return "";
        },
        variation(product) {
            if (!product.last_purchase) return 0;
            return ((product.offer_price - product.last_purchase) / product.last_purchase) * 100;
        },
        productActions(product, record, provider) {
            return [
                { label: "Análise de giro", icon: "query_stats", iconClass: "action-info", onClick: () => this.openGiroFromProduct(product, record, provider) },
                { label: "Negociar preço", icon: "handshake", iconClass: "action-success", onClick: () => this.openNegotiation(product, record, provider) },
                { label: "Alterar embalagem", icon: "inventory_2", iconClass: "action-warning", onClick: () => this.openWrap(product, record, provider) },
                { label: "Histórico de pedidos", icon: "assignment", iconClass: "action-teal", onClick: () => this.openHistory(product) },
            ];
        },
        openGiroFromProduct(product, record, provider) {
            const siblings = this.productsByOffer[this.offerKey(record, provider)] || [];

            this.$emit("giro", {
                id: product.id,
                provider_id: provider.provider.id,
                seller_id: provider.seller.id,
                name: product.name,
                packing: product.packing,
                is_generic: 0,
                main_product_id: null,
                internal_code: null,
                requested_quantity: product.quantity,
            }, siblings);
        },
        openNegotiation(product, record, provider) {
            this.negotiationModal = {
                open: true,
                winner: {
                    idVendedor: provider.seller.id,
                    idFornecedor: provider.provider.id,
                    pro_id: product.id,
                    valor: product.offer_price,
                    nomeVendedor: provider.seller.name,
                },
                product: {
                    produtoDescricao: product.name,
                    pro_observacao: null,
                    observacao: null,
                    ultimaCompra: product.last_purchase,
                },
                _record: record,
                _provider: provider,
            };
        },
        closeNegotiation() {
            const { _record, _provider } = this.negotiationModal;
            this.negotiationModal.open = false;
            this.reloadOfferProducts(_record, _provider);
        },
        openWrap(product, record, provider) {
            this.wrapModal = {
                open: true,
                winner: {
                    idVendedor: provider.seller.id,
                    idFornecedor: provider.provider.id,
                    pro_id: product.id,
                    embalagem: product.packing,
                },
                product: {
                    descricaoProduto: product.name,
                    embalagem: product.packing,
                    idProduto: product.id,
                    flag_generico: 0,
                    pro_principal: null,
                },
                _record: record,
                _provider: provider,
            };
        },
        closeWrap() {
            const { _record, _provider } = this.wrapModal;
            this.wrapModal.open = false;
            this.reloadOfferProducts(_record, _provider);
        },
        reloadOfferProducts(record, provider) {
            if (!record || !provider) return;

            this.$set(this.productsByOffer, this.offerKey(record, provider), undefined);
            this.onProviderExpand(true, record, provider);
        },
        openHistory(product) {
            this.historyModal = {
                open: true,
                productId: Number(product.id),
                productName: product.name,
            };
        },
        openProducts(row, provider) {
            this.productsModal = {
                open: true,
                storeId: row.store_id,
                storeName: row.store_name,
                providerId: provider.provider.id,
                providerName: provider.provider.name,
                sellerId: provider.seller.id,
                sellerName: provider.seller.name,
            };
        },
        sendOne(row, provider) {
            const key = row.store_id + "-" + provider.seller.id;
            this.confirm_action({
                message: `Enviar pedido para ${provider.provider.name}?`,
                confirm_text: "Enviar pedido",
                callback: () => {
                    this.$set(this.sending, key, true);
                    this.svc
                        .order({ idcotacao: this.$route.params.id, loja: row.store_id, idvendedor: provider.seller.id })
                        .then(() => {
                            this.snack("Pedido enviado com sucesso.");
                            this.$set(this.sending, key, false);
                            this.$emit("reload");
                        })
                        .catch(() => {
                            this.$set(this.sending, key, false);
                            this.snack("Não foi possível enviar o pedido.", "error");
                        });
                },
            });
        },
        sendAll(row) {
            this.confirm_action({
                message: `Enviar pedido para todos os ${row.pendingProviders.length} fornecedor(es) de ${row.store_name}?`,
                confirm_text: "Enviar todos",
                callback: () => {
                    this.$set(this.sending, row.store_id, true);
                    Promise.all(
                        row.pendingProviders.map((p) =>
                            this.svc.order({ idcotacao: this.$route.params.id, loja: row.store_id, idvendedor: p.seller.id })
                        )
                    )
                        .then(() => {
                            this.snack("Pedidos enviados com sucesso.");
                            this.$set(this.sending, row.store_id, false);
                            this.$emit("reload");
                        })
                        .catch(() => {
                            this.$set(this.sending, row.store_id, false);
                            this.snack("Não foi possível enviar um ou mais pedidos.", "error");
                        });
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.bl-card {
    --pri: var(--primary-color);
    width: 100%;

    ::v-deep(.ant-card-body) {
        width: 100%;
        padding: 0;
    }
    ::v-deep(.ant-table-wrapper),
    ::v-deep(.ant-table),
    ::v-deep(.ant-table-content),
    ::v-deep(table) {
        width: 100%;
    }
}
.bl-filterbar {
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
}
.bl-search-input {
    flex: 1 1 160px;
    min-width: 140px;
    max-width: 260px;
}
.bl-strong {
    font-weight: 600;
    color: #262626;
}
.bl-sup {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.bl-n {
    font-weight: 600;
    color: #262626;
}
.bl-v {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
}
.bl-below-min {
    color: #cf1322;
    font-weight: 600;
}
.bl-btn.ant-btn,
.bl-btn.ant-btn:hover,
.bl-btn.ant-btn:focus,
.bl-btn.ant-btn:active {
    height: 30px;
    padding: 0 14px;
    border: 1px solid var(--pri);
    background: var(--primary-color-opacity, rgba(232, 96, 44, 0.08));
    color: var(--pri);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: none;
}
.bl-btn.ant-btn[disabled],
.bl-btn.ant-btn[disabled]:hover {
    opacity: 0.5;
    cursor: not-allowed;
}
::v-deep(.ant-table-expanded-row > td) {
    padding: 0 !important;
    max-width: 0;
}
::v-deep(.ant-table-expanded-row) {
    overflow: hidden;
}
.expanded-table-wrapper {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
}
.vpm-pname {
    font-weight: 600;
    font-size: 13px;
    color: #262626;
}
.vpm-pean {
    font-size: 11px;
    color: #8c8c8c;
}
.vpm-up {
    color: #cf1322;
    font-weight: 600;
}
.vpm-down {
    color: #389e0d;
    font-weight: 600;
}
.matrix-qty-input {
    width: 70px;
    text-align: center;

    .ant-input {
        padding: 2px 4px;
    }
}
</style>
